/**
 * Registrazione del service worker Oss (/oss-sw.js, scope /oss).
 * Registrato solo nel build di produzione, oppure con REACT_APP_OSS_SW=1.
 * MAI dentro le app native OSS (user agent AffidyaOss* o window.OssApp): lì l'offline lo gestisce
 * il wrapper e un SW causerebbe solo reload e shell vecchie; eventuali SW già registrati
 * (es. il /sw.js generale di versioni precedenti) vengono rimossi.
 */
import { getOssBridge, isNativeOssApp } from "./bridge";
export const OSS_SW_URL = "/oss-sw.js";
export const OSS_SW_SCOPE = "/oss";
export const OSS_UPDATE_EVENT = "oss-sw-update";

let waitingWorker = null;
let reloading = false;
let updateRequested = false;
let registering = null;

export function ossSwEnabled(env = process.env) {
  return env.NODE_ENV === "production" || env.REACT_APP_OSS_SW === "1";
}

function announce(worker) {
  waitingWorker = worker;
  window.dispatchEvent(new CustomEvent(OSS_UPDATE_EVENT, { detail: { waiting: true } }));
}

export function isOssNativeContext(ua = typeof navigator !== "undefined" ? navigator.userAgent : "", bridge = getOssBridge()) {
  return isNativeOssApp(ua) || !!bridge;
}

/** Nel wrapper nativo: rimuove ogni service worker dell'origine (il wrapper mostra solo l'area OSS). */
export async function unregisterOssNativeServiceWorkers({ nav = typeof navigator !== "undefined" ? navigator : null } = {}) {
  if (!nav || !("serviceWorker" in nav) || typeof nav.serviceWorker.getRegistrations !== "function") return 0;
  try {
    const regs = await nav.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    return regs.length;
  } catch {
    return 0;
  }
}

export async function registerOssServiceWorker({
  nav = typeof navigator !== "undefined" ? navigator : null,
  env = process.env,
  ua = nav && nav.userAgent ? nav.userAgent : "",
  bridge = getOssBridge(),
} = {}) {
  if (!nav || !("serviceWorker" in nav)) return null;
  if (isOssNativeContext(ua, bridge)) {
    await unregisterOssNativeServiceWorkers({ nav });
    return null;
  }
  if (!ossSwEnabled(env)) return null;
  if (registering) return registering;
  registering = doRegister(nav);
  return registering;
}

async function doRegister(nav) {
  try {
    const reg = await nav.serviceWorker.register(OSS_SW_URL, { scope: OSS_SW_SCOPE });
    if (reg.waiting && nav.serviceWorker.controller) announce(reg.waiting);
    reg.addEventListener("updatefound", () => {
      const w = reg.installing;
      if (!w) return;
      w.addEventListener("statechange", () => {
        if (w.state === "installed" && nav.serviceWorker.controller) announce(w);
      });
    });
    nav.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading || !updateRequested) return;
      reloading = true;
      window.location.reload();
    });
    const check = () => reg.update().catch(() => {});
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") check();
    });
    return reg;
  } catch {
    return null;
  }
}

export function hasOssUpdate() {
  return !!waitingWorker;
}

export function applyOssUpdate() {
  if (!waitingWorker) return false;
  updateRequested = true;
  waitingWorker.postMessage({ type: "SKIP_WAITING" });
  return true;
}

export function __resetOssSw() {
  waitingWorker = null;
  reloading = false;
  updateRequested = false;
  registering = null;
}
